import React, { useMemo, useState } from "react";
import Cookies from "js-cookie";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import useNotification from "../common/Notification";
import { createCreditPayment, confirmCreditPayment } from "./billingApi";
import { formatCreditCount, rupeesToPaisa } from "./billingFormat";
import { openRazorpayCheckout, readCheckoutFields } from "./razorpayCheckout";

const BuyCreditsDialog = ({ open, onClose, onPaid }) => {
    const notify = useNotification();
    const [rupees, setRupees] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const credits = useMemo(() => rupeesToPaisa(rupees), [rupees]);
    const valid = credits != null && credits > 0;

    const handleClose = () => {
        if (busy) return;
        setError("");
        onClose();
    };

    const handlePay = async () => {
        if (!valid) {
            setError("Enter an amount greater than zero.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const payment = await createCreditPayment(Number(rupees));
            const checkout = readCheckoutFields(payment);
            const result = await openRazorpayCheckout({
                ...checkout,
                description: `Add ${formatCreditCount(credits)}`,
                name: Cookies.get("userName") || Cookies.get("name") || "",
                email: Cookies.get("email") || "",
            });
            await confirmCreditPayment(result);
            notify(`${formatCreditCount(credits)} added to the wallet`, "success");
            setRupees("");
            onPaid?.();
            onClose();
        } catch (err) {
            const message = err?.message || "Payment could not be completed";
            if (message !== "Payment was cancelled") {
                setError(message);
                notify(message, "error");
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>Buy credits</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    1 credit equals 1 paisa. ₹100 adds 10,000 credits. The amount is charged in rupees and credited after payment is confirmed.
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    label="Amount (₹)"
                    type="number"
                    value={rupees}
                    onChange={(e) => setRupees(e.target.value)}
                    inputProps={{ min: 1, step: "1" }}
                    helperText={valid ? `You will receive ${formatCreditCount(credits)}` : "Enter the rupee amount to add"}
                />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={handlePay} disabled={busy || !valid}>
                    {busy ? "Processing…" : "Continue to pay"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BuyCreditsDialog;
